import React, { useState, useEffect } from 'react';
import { X, Save, Calendar, Clock, User } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { TestPrepLessonPackage } from '../../../types';

interface CoachingSessionModalProps {
    isOpen: boolean;
    onClose: () => void;
    session?: any; // If editing
    studentId: string;
    coachingPackage?: TestPrepLessonPackage | null;
    onSave: () => void;
}

const CoachingSessionModal: React.FC<CoachingSessionModalProps> = ({ isOpen, onClose, session, studentId, coachingPackage, onSave }) => {
    const [loading, setLoading] = useState(false);
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [coachName, setCoachName] = useState('');
    const [duration, setDuration] = useState('60');
    const [notes, setNotes] = useState('');
    const [followUpPlan, setFollowUpPlan] = useState('');
    const [status, setStatus] = useState('scheduled');

    useEffect(() => {
        if (isOpen) {
            if (session) {
                const dt = new Date(session.meeting_date);
                setDate(dt.toISOString().split('T')[0]);
                setTime(dt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }));
                setCoachName(session.coach_name);
                setDuration(session.duration_minutes?.toString() || '60');
                setNotes(session.notes || '');
                setFollowUpPlan(session.follow_up_plan || '');
                setStatus(session.status);
            } else {
                const now = new Date();
                setDate(now.toISOString().split('T')[0]);
                setTime('10:00');
                setCoachName('');
                setDuration('60');
                setNotes('');
                setFollowUpPlan('');
                setStatus('scheduled');
            }
        }
    }, [isOpen, session]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            const dateTime = new Date(`${date}T${time}`);
            const previousStatus = session?.status;

            const payload: any = {
                student_id: studentId,
                coach_name: coachName,
                meeting_date: dateTime.toISOString(),
                duration_minutes: parseInt(duration),
                notes,
                follow_up_plan: followUpPlan,
                status
            };

            if (coachingPackage?.id) {
                payload.package_id = coachingPackage.id;
            }

            if (session?.id) {
                const { error } = await supabase
                    .from('testprep_coaching_sessions')
                    .update(payload)
                    .eq('id', session.id);
                if (error) throw error;
            } else {
                const { error } = await supabase
                    .from('testprep_coaching_sessions')
                    .insert(payload);
                if (error) throw error;
            }

            // Update Coaching Package used_lessons count if status is 'completed' and wasn't completed before
            if (coachingPackage?.id) {
                const isNowCompleted = status === 'completed';
                const wasCompleted = previousStatus === 'completed';

                if (isNowCompleted && !wasCompleted) {
                    const currentUsed = coachingPackage.used_lessons || 0;
                    await supabase
                        .from('testprep_lesson_packages')
                        .update({ used_lessons: currentUsed + 1 })
                        .eq('id', coachingPackage.id);
                } else if (!isNowCompleted && wasCompleted && (coachingPackage.used_lessons || 0) > 0) {
                    const currentUsed = coachingPackage.used_lessons || 0;
                    await supabase
                        .from('testprep_lesson_packages')
                        .update({ used_lessons: Math.max(0, currentUsed - 1) })
                        .eq('id', coachingPackage.id);
                }
            }

            onSave();
            onClose();
        } catch (error) {
            console.error('Error saving session:', error);
            alert('Kaydedilirken hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                            {session ? 'Görüşme Düzenle' : 'Yeni Görüşme Planla'}
                        </h2>
                        {coachingPackage && (
                            <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                                Paket: {coachingPackage.subject} (Kalan: {(coachingPackage.total_lessons || 0) - (coachingPackage.used_lessons || 0)} Görüşme)
                            </p>
                        )}
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Tarih
                            </label>
                            <div className="relative">
                                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                    type="date"
                                    required
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Saat
                            </label>
                            <div className="relative">
                                <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                    type="time"
                                    required
                                    value={time}
                                    onChange={(e) => setTime(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Koç İsmi
                        </label>
                        <div className="relative">
                            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                required
                                placeholder="Örn: Mehmet Hoca"
                                value={coachName}
                                onChange={(e) => setCoachName(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Süre (Dakika)
                        </label>
                        <input
                            type="number"
                            required
                            min="15"
                            step="15"
                            value={duration}
                            onChange={(e) => setDuration(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Görüşme Notları
                        </label>
                        <textarea
                            rows={3}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="Görüşmede konuşulanlar..."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Takip Planı / Ödevler
                        </label>
                        <textarea
                            rows={2}
                            value={followUpPlan}
                            onChange={(e) => setFollowUpPlan(e.target.value)}
                            className="w-full px-4 py-2 border border-blue-200 dark:border-blue-900 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-gray-900 dark:text-white"
                            placeholder="Bir sonraki görüşmeye kadar yapılacaklar..."
                        />
                    </div>

                    <div className="flex items-center space-x-4 pt-2">
                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="radio"
                                name="status"
                                checked={status === 'scheduled'}
                                onChange={() => setStatus('scheduled')}
                                className="text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Planlandı</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="radio"
                                name="status"
                                checked={status === 'completed'}
                                onChange={() => setStatus('completed')}
                                className="text-green-600 focus:ring-green-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Tamamlandı</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="radio"
                                name="status"
                                checked={status === 'cancelled'}
                                onChange={() => setStatus('cancelled')}
                                className="text-red-600 focus:ring-red-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">İptal</span>
                        </label>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100 dark:border-gray-700 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            Vazgeç
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                        >
                            <Save className="w-4 h-4" />
                            <span>{loading ? 'Kaydediliyor...' : 'Kaydet'}</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CoachingSessionModal;
